import React, { useEffect } from 'react';

import { Button } from 'js/design_system/Button';
import { Menu } from 'js/design_system/Menu';
import { MenuItem } from 'js/design_system/MenuItem';
import { useInBreakpoints } from 'js/design_system/hooks/useBreakpoint';
import { Options as ClickTrackingOptions } from 'js/utils/trackClick';
import { openLoginModal } from 'js/login_modal/LoginModalManager';
import CitationModal from 'js/journals/CitationModal';
import { useCitations } from './hooks/useCitations';

export type MoreActionsMenuProps = {
  workId: number;
  redirectUrl: string;
  trackClickOptions: ClickTrackingOptions;
  trigger?: React.ReactNode;
};

export const MoreActionsMenu = ({
  workId,
  redirectUrl,
  trackClickOptions,
  trigger,
}: MoreActionsMenuProps) => {
  const [citationModalOpen, setCitationModalOpen] = React.useState(false);
  const isDesktop = useInBreakpoints(['large']);
  const { citations, loadCitations } = useCitations(workId);

  useEffect(() => {
    loadCitations();
  }, [workId, loadCitations]);

  const openCitationsModal = () => {
    setCitationModalOpen(true);
  };

  const closeCitationsModal = () => {
    setCitationModalOpen(false);
  };

  const handleLoginClick = () => {
    openLoginModal({
      postLoginRedirectUrl: redirectUrl,
      clickTrackingContext: trackClickOptions,
      configuredText: {
        buttonsPage: { header: 'Read your PDF for free' },
        emailEntryPage: {
          subheader: 'Sign up or log in to continue reading.',
        },
        emailLoginPage: { subheader: 'Log in to continue reading.' },
        emailRegistrationPage: {
          subheader: 'Sign up to continue reading.',
        },
      },
      registrationParams: {
        user_source: 'download',
      },
    });
  };

  return (
    <>
      <Menu
        placement={isDesktop ? 'bottom-end' : 'top-end'}
        offset={10}
        render={() => (
          <>
            <MenuItem icon="bookmark" onClick={handleLoginClick}>
              Save
            </MenuItem>
            {citations.length > 0 && (
              <MenuItem icon="format_quote" onClick={openCitationsModal}>
                Cite
              </MenuItem>
            )}
            <MenuItem icon="share" onClick={handleLoginClick}>
              Share
            </MenuItem>
          </>
        )}
      >
        {trigger ?? <Button icon="more_vert" secondary />}
      </Menu>
      <CitationModal
        open={citationModalOpen}
        citations={citations}
        onClose={closeCitationsModal}
        source={trackClickOptions.source}
        entity_id={String(trackClickOptions.entityId)}
      />
    </>
  );
};
